import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowUpRight, Clock } from 'lucide-react';
import { blogPosts, getPostBySlug, readingTime, formatDate } from '../data/blogPosts';

/* ============================================================
   SEO — başlıq və təsviri səhifəyə uyğun dəyişir
   ============================================================ */
const useDocumentMeta = (title, description) => {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;

    const tag = document.querySelector('meta[name="description"]');
    const prevDesc = tag ? tag.getAttribute('content') : null;
    if (tag && description) tag.setAttribute('content', description);

    return () => {
      document.title = prevTitle;
      if (tag && prevDesc !== null) tag.setAttribute('content', prevDesc);
    };
  }, [title, description]);
};

/* ============================================================
   MƏQALƏ MƏTNİ
   ============================================================ */
const ArticleBody = ({ body }) => (
  <div className="space-y-6">
    {body.map((block, i) => {
      if (block.type === 'h') {
        return (
          <h2
            key={i}
            className="text-lg sm:text-xl font-serif text-gray-900 pt-6 first:pt-0"
          >
            {block.text}
          </h2>
        );
      }

      if (block.type === 'ul') {
        return (
          <ul key={i} className="space-y-2.5">
            {block.items.map((item, j) => (
              <li key={j} className="flex gap-3 text-[15px] text-gray-600 leading-relaxed">
                <span className="text-gray-300 select-none flex-shrink-0">—</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        );
      }

      if (block.type === 'q') {
        return (
          <blockquote
            key={i}
            className="border-l-2 border-gray-900 pl-5 py-1 my-8 text-[15px] sm:text-base text-gray-900 font-serif leading-relaxed"
          >
            {block.text}
          </blockquote>
        );
      }

      return (
        <p key={i} className="text-[15px] text-gray-600 leading-[1.75]">
          {block.text}
        </p>
      );
    })}
  </div>
);

/* ============================================================
   MƏQALƏ SƏHİFƏSİ
   ============================================================ */
const ArticleView = ({ post }) => {
  const navigate = useNavigate();
  useDocumentMeta(`${post.title} | L'Aliel`, post.excerpt);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [post.slug]);

  const others = blogPosts.filter((p) => p.slug !== post.slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-white pt-[73px]">
      <article className="max-w-2xl mx-auto px-6 py-12 sm:py-16">
        <button
          type="button"
          onClick={() => navigate('/blog')}
          className="inline-flex items-center gap-2 text-xs tracking-[0.15em] text-gray-400 hover:text-gray-900 transition-colors mb-10"
        >
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
          BÜTÜN YAZILAR
        </button>

        <p className="text-[11px] tracking-[0.25em] text-gray-400 mb-4">
          {post.category.toUpperCase()}
        </p>

        <h1 className="text-2xl sm:text-4xl font-serif text-gray-900 leading-[1.25] mb-5">
          {post.title}
        </h1>

        <div className="flex items-center gap-4 text-xs text-gray-400 pb-8 mb-10 border-b border-gray-100">
          <time dateTime={post.date}>{formatDate(post.date)}</time>
          <span className="inline-flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" strokeWidth={1.5} />
            {readingTime(post)} dəq
          </span>
        </div>

        <ArticleBody body={post.body} />

        {/* Məhsullara keçid */}
        <div className="mt-14 pt-10 border-t border-gray-100">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-gray-900 border-b border-gray-300 hover:border-gray-900 pb-0.5 transition-colors"
          >
            Kolleksiyaya bax
            <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>
      </article>

      {/* Digər yazılar */}
      {others.length > 0 && (
        <section className="border-t border-gray-100 bg-white">
          <div className="max-w-2xl mx-auto px-6 py-14">
            <p className="text-[11px] tracking-[0.25em] text-gray-400 mb-8">
              DİGƏR YAZILAR
            </p>
            <div className="divide-y divide-gray-100">
              {others.map((p) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  className="group flex items-start justify-between gap-4 py-5"
                >
                  <div className="min-w-0">
                    <p className="text-[10px] tracking-[0.2em] text-gray-400 mb-1.5">
                      {p.category.toUpperCase()}
                    </p>
                    <h3 className="font-serif text-gray-900 group-hover:text-gray-500 transition-colors leading-snug">
                      {p.title}
                    </h3>
                  </div>
                  <ArrowUpRight
                    className="w-4 h-4 text-gray-300 group-hover:text-gray-900 transition-colors mt-5 flex-shrink-0"
                    strokeWidth={1.5}
                  />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

/* ============================================================
   SİYAHI SƏHİFƏSİ
   ============================================================ */
const ListView = () => {
  const [activeCategory, setActiveCategory] = useState('all');

  useDocumentMeta(
    "Bloq — geyim, parça və üslub bələdçisi | L'Aliel",
    'Ölçü seçimi, parça növləri, paltar qulluğu və üslub tövsiyələri — L\'Aliel bloqu.'
  );

  const categories = useMemo(() => {
    const unique = [...new Set(blogPosts.map((p) => p.category))];
    return ['all', ...unique];
  }, []);

  const visible = useMemo(
    () =>
      activeCategory === 'all'
        ? blogPosts
        : blogPosts.filter((p) => p.category === activeCategory),
    [activeCategory]
  );

  const [featured, ...rest] = visible;

  return (
    <div className="min-h-screen bg-white pt-[73px]">
      <div className="max-w-4xl mx-auto px-6 py-12 sm:py-16">
        {/* Başlıq */}
        <header className="mb-12">
          <p className="text-[11px] tracking-[0.25em] text-gray-400 mb-3">BLOQ</p>
          <h1 className="text-3xl sm:text-4xl font-serif text-gray-900 leading-snug max-w-lg">
            Geyim, parça və üslub haqqında
          </h1>
          <p className="text-sm text-gray-500 mt-4 max-w-md leading-relaxed">
            Ölçü seçimindən parça qulluğuna qədər — praktik, işə yarayan yazılar.
          </p>
        </header>

        {/* Kateqoriya filtri */}
        <div className="flex flex-wrap gap-x-6 gap-y-2 pb-6 mb-10 border-b border-gray-100">
          {categories.map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => setActiveCategory(cat)}
              className={`text-[11px] tracking-[0.15em] pb-1 transition-colors ${
                activeCategory === cat
                  ? 'text-gray-900 border-b border-gray-900'
                  : 'text-gray-400 hover:text-gray-700'
              }`}
            >
              {cat === 'all' ? 'HAMISI' : cat.toUpperCase()}
            </button>
          ))}
        </div>

        {visible.length === 0 ? (
          <p className="text-sm text-gray-400 py-10">Bu kateqoriyada yazı yoxdur.</p>
        ) : (
          <>
            {/* Seçilmiş yazı */}
            <Link to={`/blog/${featured.slug}`} className="group block mb-4">
              <p className="text-[10px] tracking-[0.2em] text-gray-400 mb-3">
                {featured.category.toUpperCase()}
              </p>
              <h2 className="text-xl sm:text-3xl font-serif text-gray-900 group-hover:text-gray-500 transition-colors leading-snug mb-4 max-w-2xl">
                {featured.title}
              </h2>
              <p className="text-[15px] text-gray-500 leading-relaxed max-w-2xl mb-4">
                {featured.excerpt}
              </p>
              <div className="flex items-center gap-4 text-xs text-gray-400">
                <time dateTime={featured.date}>{formatDate(featured.date)}</time>
                <span>{readingTime(featured)} dəq</span>
              </div>
            </Link>

            {/* Qalanlar */}
            <div className="divide-y divide-gray-100 border-t border-gray-100 mt-10">
              {rest.map((post) => (
                <Link
                  key={post.slug}
                  to={`/blog/${post.slug}`}
                  className="group grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-6 py-8"
                >
                  <div className="sm:col-span-3">
                    <p className="text-[10px] tracking-[0.2em] text-gray-400 mb-1">
                      {post.category.toUpperCase()}
                    </p>
                    <time className="text-xs text-gray-400" dateTime={post.date}>
                      {formatDate(post.date)}
                    </time>
                  </div>

                  <div className="sm:col-span-8">
                    <h3 className="font-serif text-lg text-gray-900 group-hover:text-gray-500 transition-colors leading-snug mb-2">
                      {post.title}
                    </h3>
                    <p className="text-sm text-gray-500 leading-relaxed">{post.excerpt}</p>
                    <span className="inline-block text-xs text-gray-400 mt-3">
                      {readingTime(post)} dəq
                    </span>
                  </div>

                  <div className="hidden sm:flex sm:col-span-1 justify-end">
                    <ArrowUpRight
                      className="w-4 h-4 text-gray-300 group-hover:text-gray-900 transition-colors mt-1"
                      strokeWidth={1.5}
                    />
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/* ============================================================
   ROUTER GİRİŞİ
   /blog        -> siyahı
   /blog/:slug  -> məqalə
   ============================================================ */
const Blog = () => {
  const { slug } = useParams();

  if (!slug) return <ListView />;

  const post = getPostBySlug(slug);
  if (!post) {
    return (
      <div className="min-h-screen bg-white pt-[73px]">
        <div className="max-w-2xl mx-auto px-6 py-24 text-center">
          <h1 className="text-2xl font-serif text-gray-900 mb-3">Yazı tapılmadı</h1>
          <p className="text-sm text-gray-500 mb-8">
            Axtardığınız yazı silinmiş və ya ünvanı dəyişmiş ola bilər.
          </p>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-sm text-gray-900 border-b border-gray-300 hover:border-gray-900 pb-0.5 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
            Bütün yazılar
          </Link>
        </div>
      </div>
    );
  }

  return <ArticleView post={post} />;
};

export default Blog;
